<template>
  <div>
    <div class="d-flex justify-content-between align-items-start flex-wrap mt-1 mb-1">
      <div>
        <h4 class="mb-25">
          Template Management
        </h4>
        <p class="text-muted mb-0">
          Create and manage merchant-specific tracking page templates.
        </p>
      </div>
      <b-button
        v-if="canCreate"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        class="custom-button-color mt-50"
        @click="$emit('create')"
      >
        <feather-icon
          icon="PlusIcon"
          class="mr-25"
        />
        Create New Template
      </b-button>
    </div>
    <b-table
      hover
      responsive
      striped
      show-empty
      :items="rows"
      :fields="fields"
      class="hide-vertical-scroll"
    >
      <template #empty>
        <TableDataFetching
          :rows="rows"
          :data-loading="dataLoading"
        />
      </template>
      <template #cell(name)="data">
        <div class="d-flex align-items-center">
          <span>{{ data.value }}</span>
          <b-badge
            v-if="data.item.is_default"
            class="ml-50"
            variant="light-secondary"
          >
            Default
          </b-badge>
        </div>
      </template>
      <template #cell(type)="data">
        {{ data.item.is_default || data.item.is_system ? 'Default' : 'Custom' }}
      </template>
      <template #cell(assigned_merchants_count)="data">
        {{ data.value }}
      </template>
      <template #cell(tracking_page_link)="data">
        <span
          v-if="formatTrackingLink(data.item)"
          class="d-inline-flex align-items-center flex-nowrap text-nowrap"
        >
          <a
            :href="formatTrackingHref(data.item)"
            target="_blank"
            rel="noopener noreferrer"
            class="mr-50"
          >
            {{ formatTrackingLink(data.item) }}
          </a>
          <feather-icon
            v-b-tooltip.hover.bottom.v-primary="'Copy the tracking page URL'"
            icon="LinkIcon"
            size="16"
            class="cursor-pointer text-primary flex-shrink-0"
            @click.stop="copyTrackingLink(data.item)"
          />
        </span>
        <span v-else>---</span>
      </template>
      <template #cell(action)="data">
        <b-dropdown
          v-if="canAssign || canView"
          variant="link"
          toggle-class="text-decoration-none"
          :class="rows.length !== 1 ? 'dropdown-no-scroll' : ''"
          no-caret
          right
        >
          <template v-slot:button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="12"
              class="text-body align-middle"
            />
          </template>
          <b-dropdown-item
            v-if="canAssign"
            @click.native.stop="openAssignModal(data.item)"
          >
            <feather-icon icon="UserPlusIcon" />
            Assign Merchant
          </b-dropdown-item>
          <b-dropdown-item
            v-if="canView"
            @click="$emit('view', data.item.id)"
          >
            <feather-icon icon="EyeIcon" />
            View Template
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="auto"
          class="d-flex align-items-center mb-1 mb-md-0"
        >
          <label class="mb-0">Per page</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="pageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-1"
          />
        </b-col>
        <b-col
          class="d-flex align-items-center justify-content-md-end flex-wrap"
        >
          <span class="text-muted mr-1 text-nowrap">Showing {{ meta.from || 0 }} to {{ meta.to || 0 }} of {{ meta.total || 0 }} entries</span>
          <b-pagination
            v-model="page"
            :per-page="perPage"
            :total-rows="meta.total ? meta.total : 0"
            class="mb-0 mt-1 mt-sm-0"
            first-number
            last-number
            next-class="next-item"
            prev-class="prev-item"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <assign-tracking-template-merchants
      ref="assignMerchantModal"
      @refresh="fetchTemplates"
    />
  </div>
</template>

<script>
import {
  BButton, BTable, BBadge, BDropdown, BDropdownItem, BPagination, BRow, BCol, VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import VSelect from 'vue-select'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import { trackingPageUrl, trackingEnv } from '@/constants/config'
import store from '@/store'
import TableDataFetching from '@/components/TableDataFetching.vue'
import AssignTrackingTemplateMerchants from '@/components/setting/tracking-page/AssignTrackingTemplateMerchants.vue'

const TrackingSettingsRepository = RepositoryFactory.get('trackingSetting')

export default {
  name: 'TrackingPageTemplateList',
  components: {
    BButton,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BRow,
    BCol,
    VSelect,
    TableDataFetching,
    AssignTrackingTemplateMerchants,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      dataLoading: false,
      rows: [],
      meta: {},
      page: 1,
      perPage: 10,
      pageOptions: [10, 20, 50],
      fields: [
        { key: 'name', label: 'Template Name' },
        { key: 'type', label: 'Type' },
        {
          key: 'assigned_merchants_count', label: 'Assigned Merchants', thClass: 'text-right', tdClass: 'text-right',
        },
        { key: 'tracking_page_link', label: 'Tracking Page Link', tdClass: 'text-nowrap' },
        { key: 'action', label: 'Action' },
      ],
    }
  },
  computed: {
    canCreate() {
      return store.getters.permissions.filter(permission => permission === 'create-tracking-templates').length > 0
    },
    canView() {
      return store.getters.permissions.filter(permission => permission === 'view-tracking-templates').length > 0
    },
    canAssign() {
      return store.getters.permissions.filter(permission => permission === 'assign-merchants-to-tracking-template').length > 0
    },
  },
  watch: {
    page() {
      this.fetchTemplates()
    },
    perPage() {
      this.fetchTemplates()
    },
  },
  mounted() {
    if (this.canView) {
      this.fetchTemplates()
    }
  },
  methods: {
    async fetchTemplates() {
      if (!this.canView) {
        return
      }
      this.dataLoading = true
      this.rows = []
      try {
        const { data } = await TrackingSettingsRepository.getTrackingPageTemplates(this.page, this.perPage)
        this.rows = data.data || []
        this.meta = data.meta || {}
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    formatTrackingHref(item) {
      return this.buildTrackingHref(item.tracking_page_link)
    },
    buildTrackingHref(link) {
      if (!link) {
        return ''
      }
      let href = String(link).trim()
      if (!href.startsWith('http')) {
        if (href.includes('.')) {
          href = `https://${href}`
        } else {
          const base = (trackingPageUrl || '').replace(/\/$/, '')
          href = `${base}${href.startsWith('/') ? href : `/${href}`}`
        }
      }
      const infix = trackingEnv || (window.location.host.includes('.int.curfox.com') ? 'int' : '')
      if (!infix || href.includes(`tracking.${infix}.curfox.com`)) {
        return href
      }
      return href.replace(/tracking\.curfox\.com(?=[/:?]|$)/, `tracking.${infix}.curfox.com`)
    },
    formatTrackingLink(item) {
      const href = this.formatTrackingHref(item)
      return href.replace(/^https?:\/\//, '')
    },
    copyTrackingLink(item) {
      const urlText = this.formatTrackingHref(item)
      if (!urlText) {
        return
      }
      try {
        const textarea = document.createElement('textarea')
        textarea.value = urlText
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.showSuccessMessage('Link has been copied successfully')
      } catch (error) {
        this.showErrorMessage('Failed to copy link')
      }
    },
    openAssignModal(item) {
      this.$nextTick(() => {
        if (this.$refs.assignMerchantModal) {
          this.$refs.assignMerchantModal.openModal(item)
        }
      })
    },
  },
}
</script>
<style scoped>
.per-page-selector {
  width: 110px;
  flex-shrink: 0;
}
</style>
